import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';
import SearchIcon from '../../../../components/icons/SearchIcon';
import { useAuth } from '../../../../contexts/AuthContext';
import { AI_EDUTECH_TRAININGS, TrainingMaterialRecord } from '../../../../data/aiEdutechTrainings';
import { canManageOwnContent, teacherTrainingManagePath } from '../../../../config/contentRoutes';

const AIEdutechMainpage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');

  const canManage = canManageOwnContent(currentUser?.role);

  const sharingMaterials = useMemo(
    () =>
      AI_EDUTECH_TRAININGS.filter(
        (material) =>
          material.status === 'sharing' &&
          material.name.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [searchQuery]
  );

  const handleOpen = (material: TrainingMaterialRecord) => {
    if (material.type === 'TSX' && material.tsxPath) {
      navigate(material.tsxPath);
      return;
    }
    if (material.type === 'URL' && material.url) {
      window.open(material.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (material.type === 'PDF' && material.fileUrl) {
      window.open(material.fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        padding: '24px',
        boxSizing: 'border-box',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: '0 0 8px', fontSize: '32px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
              AI/에듀테크 연수
            </h1>
            <p style={{ margin: 0, fontSize: '15px', color: 'var(--Color-text-normal)', lineHeight: 1.6 }}>
              진행 중인 AI·에듀테크 직무연수 자료를 확인하고 실습 페이지로 이동할 수 있습니다.
            </p>
          </div>
          {canManage && (
            <Button variant="outline" size="medium" onClick={() => navigate(teacherTrainingManagePath('ai-edutech'))}>
              내 연수 관리
            </Button>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'var(--Color-background-normal)',
            borderRadius: '999px',
            border: '1px solid var(--Color-line-normal)',
            maxWidth: '480px',
          }}
        >
          <SearchIcon width={20} height={20} color="#222222" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="연수 검색"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: '14px', background: 'transparent' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
          {sharingMaterials.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--Color-text-normal)', fontSize: '15px' }}>표시할 연수가 없습니다.</p>
          ) : (
            sharingMaterials.map((material) => (
              <Card
                key={material.id}
                padding="20px"
                borderRadius="16px"
                background="var(--Color-background-normal)"
                style={{ cursor: 'pointer', height: '100%' }}
                onClick={() => handleOpen(material)}
              >
                <div
                  style={{
                    display: 'inline-block',
                    marginBottom: '12px',
                    padding: '2px 10px',
                    borderRadius: '8px',
                    backgroundColor: '#FFD2B5',
                    color: 'var(--Color-fill-brand-color)',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {material.type}
                </div>
                <h2 style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: 'var(--Color-text-strong)', lineHeight: 1.4 }}>
                  {material.name}
                </h2>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--Color-text-normal)' }}>
                  공유 기간: {material.sharePeriod.start} ~ {material.sharePeriod.end}
                </p>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AIEdutechMainpage;
